import { CHART_MAX_CATEGORIES, otherLabel } from 'api-models'
import {
	AnalyticsDimensionId,
	AnalyticsMetricId,
	BreakdownResult
} from 'services/user'
import { displayName } from './display-names'
import { colorFor, OTHER_COLOR } from './chart-palette'

// Adapters between the chart layer (which speaks metric/dimension LABELS from the
// layout config) and the aggregation engine (which speaks ids), plus the mappers
// from a live breakdown to the chart's category / stack shapes. Keeps the
// label-based config intact while sourcing data live.
//
// DEV-2432: every chart variable is capped at its top ten (the engine already
// ranks and cuts; `totalCategories` says whether it had to), the tail is a grey
// "Other (N)" bucket only where the ticket allows one (donut; stack segments),
// raw values are mapped to display names by `key`, and each category carries the
// icon hint the dimension table renders beside the same value.

export const CHART_METRIC_LABEL_TO_ID: Record<string, AnalyticsMetricId> = {
	'Ad clicks analyzed': 'analyzed_ad_clicks',
	'Valid clicks': 'valid_ad_clicks',
	'Suspicious clicks': 'suspicious_ad_clicks',
	'Bad clicks': 'bad_ad_clicks',
	'Bad traffic rate': 'bad_traffic_rate',
	'Blocked sources': 'blocked_sources',
	'Prevented ad waste': 'prevented_ad_waste'
}

// The three fixed chart dimensions (positions 1/2/3) + the rest of the taxonomy,
// so the mapper works if a dimension is ever surfaced elsewhere.
export const DIM_LABEL_TO_ID: Record<string, AnalyticsDimensionId> = {
	'IP usage': 'ip_usage',
	Status: 'status',
	Keyword: 'keyword',
	Country: 'country',
	Region: 'region',
	City: 'city',
	'Ad platform': 'ad_platform',
	'Ad account': 'ad_account',
	'Campaign type': 'campaign_type',
	Campaign: 'campaign',
	'Ad group / Ad set': 'ad_group',
	ISP: 'isp',
	'Device type': 'device_type',
	'Operating system': 'os',
	Browser: 'browser',
	Placement: 'placement',
	'Target URL': 'target_url',
	utm_source: 'utm_source',
	utm_campaign: 'utm_campaign',
	utm_content: 'utm_content',
	utm_medium: 'utm_medium',
	utm_term: 'utm_term'
}

export const metricIdForLabel = (label: string): AnalyticsMetricId | undefined =>
	CHART_METRIC_LABEL_TO_ID[label]
export const dimensionIdForLabel = (
	label: string
): AnalyticsDimensionId | undefined => DIM_LABEL_TO_ID[label]

// ── Category (bar / donut) shape ────────────────────────────────────────────
export interface ChartCat {
	/** Raw dimension value (the engine's key); `__other__` for the bucket. */
	key: string
	/** Display name (§2.3). */
	label: string
	value: number
	/** Share of the whole breakdown (rows + Other), one decimal. */
	pct: number
	color: string
	/** Dimension the value belongs to, for the icon ladder. */
	dimensionId?: AnalyticsDimensionId
	/** Icon hint from the engine (ad-platform code / country ISO). */
	meta?: string | null
	/** Only on the "Other" bucket: how many categories it rolls up. */
	otherCount?: number
}

const share = (v: number, grand: number) => (grand ? +((v / grand) * 100).toFixed(1) : 0)

/**
 * Map a live breakdown into chart categories, ranked by the metric, descending.
 * A donut keeps the grey "Other (N)" slice so the ring stays the whole; a bar
 * is a ranking and shows the top ten alone (§1 "Where Other appears").
 */
export const breakdownToCats = (
	result: BreakdownResult | undefined,
	dimensionId?: AnalyticsDimensionId,
	chartType?: string
): ChartCat[] => {
	if (!result) return []
	const rows = [...result.rows]
		.sort((a, b) => b.value - a.value)
		.slice(0, CHART_MAX_CATEGORIES)
	const includeOther = chartType === 'Pie' && !!result.other
	const grand =
		rows.reduce((s, r) => s + r.value, 0) + (includeOther ? result.other?.value ?? 0 : 0)
	const cats: ChartCat[] = rows.map((r, i) => ({
		key: r.key,
		label: displayName(dimensionId, r.key, r.label),
		value: r.value,
		pct: share(r.value, grand),
		color: colorFor(dimensionId, r.key, i),
		dimensionId,
		meta: r.meta ?? null
	}))
	if (includeOther && result.other) {
		const count = result.other.count ?? 0
		cats.push({
			key: '__other__',
			label: otherLabel(count),
			value: result.other.value,
			pct: share(result.other.value, grand),
			color: OTHER_COLOR,
			dimensionId,
			meta: null,
			otherCount: count
		})
	}
	return cats
}

// ── Stacked shape ───────────────────────────────────────────────────────────
export interface ChartStackKey {
	key: string
	label: string
	color: string
	meta?: string | null
	/** True for the per-bar remainder bucket (always last, always grey). */
	isOther?: boolean
}
export interface ChartStackSeg {
	key: string
	label: string
	value: number
	color: string
	meta?: string | null
	isOther?: boolean
}
export interface ChartStackCol {
	key: string
	label: string
	meta?: string | null
	total: number
	/** In `segKeys` order (bottom-to-top stacking order), Other last. */
	segs: ChartStackSeg[]
}
export interface ChartStack {
	cols: ChartStackCol[]
	/** The global top-10 stack categories (+ Other when the tail was rolled). */
	segKeys: ChartStackKey[]
	dimensionId?: AnalyticsDimensionId
	dimension2Id?: AnalyticsDimensionId
}

const EMPTY_STACK: ChartStack = { cols: [], segKeys: [] }

/**
 * Build a Stacked-bar dataset from a two-dimension breakdown: one column per
 * top-ten primary value, split into the engine's global top-ten stack
 * categories (same order and colour in every bar) plus, where a bar had more,
 * that bar's own grey "Other (N)".
 */
export const breakdownToStack = (
	result: BreakdownResult | undefined,
	dimensionId?: AnalyticsDimensionId,
	dimension2Id?: AnalyticsDimensionId
): ChartStack => {
	if (!result?.stack) return EMPTY_STACK
	const { keys, otherCount } = result.stack
	const segKeys: ChartStackKey[] = keys.map((k, i) => ({
		key: k.key,
		label: displayName(dimension2Id, k.key, k.label),
		color: colorFor(dimension2Id, k.key, i),
		meta: k.meta ?? null
	}))
	const anyOther = result.rows.some((r) => (r.stack?.other ?? 0) > 0)
	if (anyOther) {
		segKeys.push({
			key: '__other__',
			label: otherLabel(otherCount),
			color: OTHER_COLOR,
			meta: null,
			isOther: true
		})
	}
	const cols: ChartStackCol[] = [...result.rows]
		.sort((a, b) => b.value - a.value)
		.slice(0, CHART_MAX_CATEGORIES)
		.map((r) => {
			const values = r.stack?.values ?? []
			const segs: ChartStackSeg[] = keys.map((k, i) => ({
				key: k.key,
				label: segKeys[i].label,
				value: values[i] ?? 0,
				color: segKeys[i].color,
				meta: k.meta ?? null
			}))
			const other = r.stack?.other ?? 0
			if (other > 0) {
				segs.push({
					key: '__other__',
					label: otherLabel(r.stack?.otherCount ?? 0),
					value: other,
					color: OTHER_COLOR,
					meta: null,
					isOther: true
				})
			}
			return {
				key: r.key,
				label: displayName(dimensionId, r.key, r.label),
				meta: r.meta ?? null,
				total: segs.reduce((s, g) => s + g.value, 0),
				segs
			}
		})
	return { cols, segKeys, dimensionId, dimension2Id }
}

// ── Panel title ─────────────────────────────────────────────────────────────
/** Two halves: the metric on top, the variables under it. A variable the chart
 *  had to cut reads "(Top 10)" right after its name (§1). */
export interface PanelTitle {
	metric: string
	by: string
}

const topLabel = (name: string, total: number | undefined) =>
	total !== undefined && total > CHART_MAX_CATEGORIES
		? `${name} (Top ${CHART_MAX_CATEGORIES})`
		: name

export const panelTitle = (
	cfg: { metric: string; dimension: string; dimension2?: string; chartType: string },
	result?: BreakdownResult
): PanelTitle => {
	const primary = topLabel(cfg.dimension, result?.totalCategories)
	const stackBy =
		cfg.chartType === 'Stacked bar' && cfg.dimension2 && cfg.dimension2 !== cfg.dimension
			? cfg.dimension2
			: undefined
	return {
		metric: cfg.metric,
		by: stackBy
			? `by ${primary} and ${topLabel(stackBy, result?.stack?.totalCategories)}`
			: `by ${primary}`
	}
}

/** The one-line form of a panel title, for the PDF and the render route. */
export const panelTitleText = (title: PanelTitle) => `${title.metric} ${title.by}`
